import React from 'react';
import { useRouter } from 'next/router';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'next-i18next';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import UrlMapper from '@/config/url-mapper';
import { useForgotPassword } from '@/services/auth';
import { Asterisk, Button, Textfield, Typography } from '@/components/atoms';
import { UnexpectedError } from '@/components/molecules';
import { ButtonContainer, CustomForm, TextWrapper } from './ForgotForm.styles';

type ForgotFormType = {
    email: string;
};

const ForgotForm: React.FC = () => {
    const { t } = useTranslation(); // Translations
    const router = useRouter(); // Router
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [globalError, setGlobalError] = React.useState<boolean>(false);
    const { forgotPassword, loading } = useForgotPassword();

    // forms initial values
    const initialValues = {
        email: '',
    };

    // Validation schemes
    const validationSchema = Yup.object({
        email: Yup.string().email(t('auth:forgot-form.invalid.email')).required(t('auth:forgot-form.required.email')),
    });

    const handleOnSubmit = async (
        values: ForgotFormType,
        {
            setFieldError,
        }: {
            setFieldError: (field: string, message: string | undefined) => void;
        }
    ) => {
        setGlobalError(false);

        if (executeRecaptcha) {
            const token = await executeRecaptcha('FORGOT_PASSWORD');

            await forgotPassword({
                variables: {
                    ...values,
                    recaptchaToken: token,
                    callbackUrl: UrlMapper.forgot_password_callback,
                },
                onCompleted: async (data) => {
                    if (data?.forgotPassword?.status === 200) {
                        await router.push({
                            pathname: UrlMapper.forgot_password_verification,
                        });
                    } else {
                        if (data?.forgotPassword?.errors && data?.forgotPassword?.errors?.length > 0) {
                            data?.forgotPassword?.errors?.map((err) => {
                                const field = err?.field || '';
                                const message = err?.messages.join(',') || '';
                                if (!field) {
                                    setGlobalError(true);
                                } else {
                                    setFieldError(field, message);
                                }
                            });
                        } else {
                            setGlobalError(true);
                        }
                    }
                },
                onError: async () => {
                    setGlobalError(true);
                },
            });
        }
    };

    return (
        <>
            <Typography as="h1" variant="h6" align="center">
                {t('auth:forgot-form.section.title')}
            </Typography>

            <TextWrapper>
                {globalError && <UnexpectedError />}

                {!globalError && (
                    <Typography variant="body2" align="center">
                        {t('auth:forgot-form.section.description')}
                    </Typography>
                )}
            </TextWrapper>

            <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleOnSubmit}>
                {({ errors, values, touched, handleChange, handleBlur }) => {
                    return (
                        <CustomForm>
                            <Textfield
                                id="email"
                                type="text"
                                label={<Asterisk text={t('auth:forgot-form.label.email')} />}
                                placeholder={t('auth:forgot-form.placeholder.email')}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values?.email}
                                errors={touched.email && errors.email}
                                success={(touched.email || values?.email !== '') && !errors.email}
                            />
                            <ButtonContainer>
                                <Button
                                    content={t('auth:forgot-form.button.back-to-login')}
                                    type="button"
                                    variant="outlined"
                                    onClick={() => void router.push(UrlMapper.login)}
                                    disabled={loading}
                                />
                                <Button content={t('auth:forgot-form.button.recover')} type="submit" variant="filled" disabled={loading} isSubmitting={loading} />
                            </ButtonContainer>
                        </CustomForm>
                    );
                }}
            </Formik>
        </>
    );
};

export default ForgotForm;
